import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type {
  Coach,
  CoachType,
  CoachStatus,
  CoachCohortAssignment,
  CoachActivityLog,
  CreateCoachRequest,
  UpdateCoachRequest,
  PaginatedResponse,
} from '../../types';

// Options returned by the API
export interface CoachOptions {
  coachTypes: readonly string[];
  statuses: readonly string[];
  industries: readonly string[];
  raceEthnicity: readonly string[];
  genderIdentity: readonly string[];
  pronouns: readonly string[];
  learningExperiences: readonly string[];
}

export interface CoachListParams extends BaseListParams {
  status?: CoachStatus | CoachStatus[];
  coach_type?: CoachType;
}

export const coachesApi = {
  /**
   * Get all coaches with optional search, status filter, and pagination
   */
  list: async (params?: CoachListParams): Promise<PaginatedResponse<Coach>> => {
    const url = `/api/coaches${buildQueryString(params)}`;
    return apiClient.getPaginated<Coach>(url);
  },

  /**
   * Get a single coach by ID with user details
   */
  getById: async (id: string): Promise<Coach> => {
    return apiClient.get<Coach>(`/api/coaches/${id}`);
  },

  /**
   * Get coach's current and past cohort assignments
   */
  getCohorts: async (id: string): Promise<{ current: CoachCohortAssignment[]; past: CoachCohortAssignment[] }> => {
    return apiClient.get<{ current: CoachCohortAssignment[]; past: CoachCohortAssignment[] }>(`/api/coaches/${id}/cohorts`);
  },

  /**
   * Create a new coach (creates user + persona + coach_profile)
   */
  create: async (data: CreateCoachRequest): Promise<Coach> => {
    return apiClient.post<Coach>('/api/coaches', data);
  },

  /**
   * Update a coach (updates user and coach_profile)
   */
  update: async (id: string, data: UpdateCoachRequest): Promise<Coach> => {
    return apiClient.put<Coach>(`/api/coaches/${id}`, data);
  },

  /**
   * Delete a coach (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/coaches/${id}`);
  },

  /**
   * Check if an email is available for a new coach
   */
  checkEmail: async (email: string): Promise<{ available: boolean }> => {
    return apiClient.get<{ available: boolean }>(`/api/coaches/check-email/${encodeURIComponent(email)}`);
  },

  /**
   * Upload a coach's avatar image
   */
  uploadAvatar: async (id: string, file: File): Promise<Coach> => {
    const formData = new FormData();
    formData.append('avatar', file);
    return apiClient.postFormData<Coach>(`/api/coaches/${id}/avatar`, formData);
  },

  /**
   * Get dropdown options for coach forms
   */
  getOptions: async (): Promise<CoachOptions> => {
    return apiClient.get<CoachOptions>('/api/coaches/options');
  },

  /**
   * Get activity log for a coach
   */
  getActivityLog: async (id: string): Promise<CoachActivityLog[]> => {
    return apiClient.get<CoachActivityLog[]>(`/api/coaches/${id}/activity`);
  },

  /**
   * Send or resend SGOS invitation email with magic link
   */
  sendInvitation: async (id: string): Promise<{ sent: boolean; isResend: boolean; expiresAt: string }> => {
    return apiClient.post<{ sent: boolean; isResend: boolean; expiresAt: string }>(`/api/coaches/${id}/send-invitation`);
  },
};

export default coachesApi;
